import { useEffect, useMemo } from 'react';
import {
  CourtPageCoachCardFieldsFragment,
  useGetCourtPageV2AvailabilityCoachesLazyQuery,
  useGetCourtPageV2StaffCoachesLazyQuery,
} from 'types/generated/client';

export type VenueCoach = CourtPageCoachCardFieldsFragment;

/**
 * Coaches for a venue: staff coaches first (venue-managed), then coaches with
 * availability at the venue. Both queries fire in parallel; staff duplicates
 * are filtered out of the availability list client-side.
 */
export const useVenueCoaches = ({ venueId }: { venueId?: string }) => {
  const [getStaffCoaches, { data: staffData, loading: isStaffLoading, called: isStaffCalled }] =
    useGetCourtPageV2StaffCoachesLazyQuery();
  const [
    getAvailabilityCoaches,
    { data: availabilityData, loading: isAvailabilityLoading, called: isAvailabilityCalled },
  ] = useGetCourtPageV2AvailabilityCoachesLazyQuery();

  useEffect(() => {
    if (venueId) {
      Promise.all([
        getStaffCoaches({ variables: { venueId } }),
        getAvailabilityCoaches({ variables: { venueId, excludeUserIds: [] } }),
      ]);
    }
  }, [venueId]);

  const coaches = useMemo<VenueCoach[]>(() => {
    const staffCoaches = (staffData?.venueStaffCoaches || [])
      .map((staff) => staff.coachProfile)
      .filter(Boolean) as VenueCoach[];
    const staffCoachIds = new Set(staffCoaches.map((coach) => coach.id));
    const availabilityCoaches = (availabilityData?.userProfiles || []).filter(
      (coach) => !staffCoachIds.has(coach.id),
    );

    return [...staffCoaches, ...availabilityCoaches];
  }, [staffData, availabilityData]);

  const isLoading =
    !isStaffCalled || isStaffLoading || !isAvailabilityCalled || isAvailabilityLoading;

  return { coaches, isLoading };
};
